/**
 * 模块: 趋势分析主容器
 * 职责: 组装趋势分析页面的各个组件
 * 限制: 文件<280行
 */

import React from 'react';
import { useParams } from 'react-router-dom';
import { Button, Space, Spin, Row, Col } from 'antd';
import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import { useTrendData } from './useTrendData';
import { TrendFilters } from './TrendFilters';
import { TrendCharts } from './TrendCharts';
import type { Period } from '../../types';
import './TrendAnalysis.css';

export const TrendAnalysis: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const {
    loading,
    sku,
    periods,
    trendData,
    selectedMetrics,
    setSelectedMetrics,
    selectedPeriods,
    setSelectedPeriods,
    chartType,
    setChartType,
    dateRange,
    setDateRange,
    networkData,
    trajectoryData,
    marketShiftData,
    loadData,
    handleExport,
  } = useTrendData(id);

  const handleSelectAllPeriods = () => {
    const allPeriods = periods.map((p: Period) => p.id.toString());
    setSelectedPeriods(allPeriods);
  };

  if (loading && !trendData.length) {
    return (
      <div className="loading-container">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="trend-analysis-page">
      <div className="page-header">
        <div>
          <h1>趋势分析</h1>
          {sku && <p className="sku-name">{sku.name} ({sku.asin})</p>}
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={loadData}>
            刷新
          </Button>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleExport}
            disabled={trendData.length === 0}
          >
            导出数据
          </Button>
        </Space>
      </div>

      <Row gutter={24}>
        <Col span={6}>
          <TrendFilters
            selectedMetrics={selectedMetrics}
            selectedPeriods={selectedPeriods}
            periods={periods}
            dateRange={dateRange}
            trendDataLength={trendData.length}
            onMetricChange={setSelectedMetrics}
            onPeriodChange={setSelectedPeriods}
            onDateRangeChange={setDateRange}
            onSelectAllPeriods={handleSelectAllPeriods}
          />
        </Col>
        <Col span={18}>
          <TrendCharts
            loading={loading}
            trendData={trendData}
            selectedMetrics={selectedMetrics}
            chartType={chartType}
            networkData={networkData}
            trajectoryData={trajectoryData}
            marketShiftData={marketShiftData}
            onChartTypeChange={setChartType}
          />
        </Col>
      </Row>
    </div>
  );
};

